<template>
  <v-row>
    <v-col cols="12">
      <v-alert dense type="info">
        {{ item.infoText || 'An welchen Standorten möchtest du weiterhin unterrichten?' }}
      </v-alert>

      <!-- Aktuelle Standorte -->
      <v-subheader class="mt-4">Aktuelle Standorte</v-subheader>
      <div
          v-for="institution in additionalData.currentInstitutions"
          :key="institution._id.toString()"
          class="mb-2 pa-3 d-flex align-center"
          style="border:1px solid rgba(0,0,0,0.1); border-radius:8px;"
      >
        <v-simple-checkbox
            :value="selectedMap[institution._id.toString()]"
            class="d-flex align-center mr-2"
            color="info"
            @input="handleChange(institution._id.toString(), $event, true)"
        />
        <v-list-item-content style="font-size: 16px; line-height: 20px; font-weight: 400;">
          <v-list-item-title>{{ institution.name }}</v-list-item-title>
          <v-list-item-subtitle v-if="institution.courseTimes">
            {{ serializeCourseTimes(institution.courseTimes) }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </div>

      <!-- Weitere Standorte -->
      <v-subheader
          v-if="additionalData.availableInstitutions.length"
          class="mt-4"
      >
        Weitere Standorte
      </v-subheader>
      <div
          v-for="institution in additionalData.availableInstitutions"
          :key="institution._id.toString()"
          class="mb-2 pa-3"
          style="border:1px solid rgba(0,0,0,0.1); border-radius:8px;"
      >
        <v-checkbox
            :input-value="selectedMap[institution._id.toString()]"
            color="info"
            dense
            hide-details
            @change="handleChange(institution._id.toString(), $event, false)"
        >
          <template v-slot:label>
            <v-list-item-content>
              <v-list-item-title>
                {{ institution.name }}
              </v-list-item-title>
              <v-list-item-subtitle v-if="institution.travelDistance > 0">
                Entfernung: {{ (institution.travelDistance / 1000).toFixed(1) }} km / Dauer:
                {{ Math.round(institution.travelDuration / 60) }} min
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="!institution.hasCourses" class="success--text">
                Aktuell keine Kurse
              </v-list-item-subtitle>
              <div v-else>
                <v-list-item-subtitle v-if="institution.occupationState">
                  <v-tooltip right transition="slide-x-transition">
                    <template v-slot:activator="{on}">
                      <span v-on="on">{{OccupationStateRecord[institution.occupationState]?.text}}</span>
                    </template>
                    <span>{{OccupationStateRecord[institution.occupationState]?.description}}</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="institution.courseTimes">
                  {{ serializeCourseTimes(institution.courseTimes) }}
                </v-list-item-subtitle>
              </div>
            </v-list-item-content>
          </template>
        </v-checkbox>
      </div>

      <!-- Warn-Dialog -->
      <v-dialog v-model="confirmDialog" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="500">
        <v-card>
          <v-card-title class="error--text">Standort abgeben?</v-card-title>
          <v-card-text>
            Bist du dir sicher, dass du an diesem Standort nicht mehr unterrichten möchtest?<br>
            Dies hat zur Folge, dass wir den Standort an eine andere Person vergeben.
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn color="info" text @click="cancelRemoval">Abbrechen</v-btn>
            <v-btn color="error" text @click="confirmRemoval">Ja, ich bin sicher.</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-btn v-if="step > 1" block depressed @click="$emit('update:step', step - 1)">
        Zurück
      </v-btn>

      <v-btn
          block
          class="my-4"
          color="info"
          @click="goNext"
      >
        Weiter
      </v-btn>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import {LeanDocument} from 'mongoose';
import {SurveyItemType} from '@/enums/survey-manager/SurveyItemType.enum';
import {SurveyItemResponseType} from '@/enums/survey-manager/SurveyItemResponseType.enum';
import {AssignedInstitutionsSelection, UserSurveyDocument} from '@/documentTypes/survey-manager/UserSurvey.document';
import {
  AssignedInstitutionsSurveyDataResponseItem,
  SurveyDataAvailableInstitutionItem,
  SurveyDataInstitutionItem,
} from '@/classes/dto/survey-manager/response/GetSurveyData.response.dto';
import {Weekdays} from '@/helpers/dateHelpers/Weekdays.enum';
import {VueRecord} from '@/helpers/VueCompatibleRecord';
import {SurveyDataInstitutionOccupationState} from '@/enums/survey-manager/SurveyDataInstitutionOccupationState.enum';

moment.locale('de');

export default Vue.extend({
  name: 'AssignedInstitutionSurveyInput',
  props: {
    item: {
      type: Object as () => AssignedInstitutionsSelection & {
        responseType:
            | SurveyItemResponseType.INSTITUTION_CONFIRMATION_NEXT_PERIOD
            | SurveyItemResponseType.TEXT_ONLY;
      },
      required: true,
    },
    step: {type: Number, required: true},
    survey: {
      type: Object as () => LeanDocument<UserSurveyDocument>,
      required: true,
    },
    additionalData: {
      type: Object as () => AssignedInstitutionsSurveyDataResponseItem,
      required: true,
    },
  },
  data: () => ({
    selectedMap: {} as Record<string, boolean>,
    confirmDialog: false,
    pendingId: '' as string,
    OccupationStateRecord: SurveyDataInstitutionOccupationState.Record as VueRecord<typeof SurveyDataInstitutionOccupationState.Record>,
  }),
  mounted() {
    const map: Record<string, boolean> = {};
    this.additionalData.currentInstitutions.forEach((i: SurveyDataInstitutionItem) => {
      map[i._id.toString()] = true;
    });
    this.additionalData.availableInstitutions.forEach((i: SurveyDataAvailableInstitutionItem) => {
      map[i._id.toString()] = false;
    });
    this.selectedMap = map;
  },
  methods: {
    handleChange(id: string, checked: boolean, isCurrent: boolean) {
      if (this.confirmDialog) {
        return;
      }

      // Aktuellen Standort abwählen → Dialog öffnen
      if (isCurrent && !checked) {
        // Sofort wieder zurücksetzen → dann Dialog öffnen
        this.selectedMap[id] = true;
        this.pendingId = id;
        this.confirmDialog = true;
        return;
      }

      // Normales toggling
      this.selectedMap[id] = checked;
    },
    cancelRemoval() {
      // Beim Abbrechen bleibt die Checkbox checked
      this.$set(this.selectedMap, this.pendingId, true);
      this.pendingId = '';
      this.confirmDialog = false;
    },
    confirmRemoval() {
      // Wirklich abwählen
      this.$set(this.selectedMap, this.pendingId, false);
      this.pendingId = '';
      this.confirmDialog = false;
    },
    goNext() {
      // Current Institutions
      const confirmed = this.additionalData.currentInstitutions
          .map(i => i._id.toString())
          .filter(id => this.selectedMap[id]);

      const declined = this.additionalData.currentInstitutions
          .map(i => i._id.toString())
          .filter(id => !this.selectedMap[id]);

      // Available Institutions
      const possible = this.additionalData.availableInstitutions
          .map(i => i._id.toString())
          .filter(id => this.selectedMap[id]);

      this.$emit('responseItem', {
        type: SurveyItemType.ASSIGNED_INSTITUTIONS_SELECTION,
        confirmed,
        declined,
        possible,
      });
      this.$emit('update:step', this.step + 1);
    },
    serializeCourseTimes(courseTimes: SurveyDataInstitutionItem['courseTimes']) {
      if (!courseTimes) {
        return '';
      } else {
        courseTimes = [...courseTimes];
        courseTimes?.sort((a, b) => {
          if (a.dayOfWeek === b.dayOfWeek) {
            return a.startTime - b.startTime;
          }
          return a.dayOfWeek - b.dayOfWeek;
        });
        let result = '';
        for (const t of courseTimes) {
          const start = moment().startOf('day').add(t.startTime, 'minutes');
          const end = moment().startOf('day').add(t.endTime, 'minutes');
          const day = Weekdays.Record[t.dayOfWeek]?.text;
          result = `${day}: ${start.format('HH:mm')} - ${end.format('HH:mm')}`;
        }
        return result;
      }
    },
  },
});
</script>
